import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Brain, 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle, 
  Clock, 
  Target,
  BookOpen,
  FileText,
  HelpCircle,
  Lightbulb
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ContentGap {
  area: string;
  severity: 'high' | 'medium' | 'low';
  description: string;
}

interface ContentRecommendation {
  type: 'mcq' | 'note' | 'topic' | 'case_study';
  title: string;
  priority: 'high' | 'medium' | 'low';
  reason?: string;
  description?: string;
  difficulty?: number;
  estimated_time?: string;
  tags?: string[];
}

interface ContentMetrics {
  coverage_score: number;
  difficulty_balance: string;
  content_density: string;
}

interface AnalysisResult {
  gaps?: ContentGap[];
  recommendations?: ContentRecommendation[];
  metrics?: ContentMetrics;
  suggestions?: ContentRecommendation[];
  focus_areas?: string[];
  learning_objectives?: string[];
}

export default function ContentSuggestions() {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [selectedTopic, setSelectedTopic] = useState<string>("");
  const [topics, setTopics] = useState<Array<{id: string, name: string}>>([]);
  const { toast } = useToast();

  const loadTopics = async () => {
    const { data } = await supabase.from('topics').select('id, name').order('name');
    setTopics(data || []);
  };

  // Load topics on component mount
  useEffect(() => {
    loadTopics();
  }, []);

  const runAnalysis = async (type: 'content-gaps' | 'topic-suggestions') => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('ai-content-suggestions', {
        body: {
          analysisType: type,
          topicId: selectedTopic && selectedTopic !== 'all' ? selectedTopic : null
        }
      });

      if (error) throw error;

      if (data.success) {
        setAnalysis(data.analysis);
        toast({
          title: "Analysis Complete",
          description: `${type === 'content-gaps' ? 'Content gap analysis' : 'Topic suggestions'} generated successfully.`
        });
      } else {
        throw new Error(data.error);
      }
    } catch (error) {
      console.error('Analysis error:', error);
      toast({
        title: "Analysis Failed",
        description: "Failed to generate content analysis. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'default';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'high': return <AlertTriangle className="h-4 w-4 text-destructive" />;
      case 'medium': return <Clock className="h-4 w-4 text-warning" />;
      case 'low': return <CheckCircle className="h-4 w-4 text-success" />;
      default: return <HelpCircle className="h-4 w-4" />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'mcq': return <HelpCircle className="h-4 w-4" />;
      case 'note': return <FileText className="h-4 w-4" />;
      case 'topic': return <BookOpen className="h-4 w-4" />;
      case 'case_study': return <Target className="h-4 w-4" />;
      default: return <Lightbulb className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <div className="flex-1">
          <Select value={selectedTopic} onValueChange={setSelectedTopic}>
            <SelectTrigger>
              <SelectValue placeholder="Select topic (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Topics</SelectItem>
              {topics.filter(topic => topic.id && topic.id.trim() !== '').map((topic) => (
                <SelectItem key={topic.id} value={topic.id}>
                  {topic.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button 
          onClick={() => runAnalysis('content-gaps')} 
          disabled={loading}
          variant="outline"
        >
          <Brain className="mr-2 h-4 w-4" />
          Analyze Gaps
        </Button>
        <Button 
          onClick={() => runAnalysis('topic-suggestions')} 
          disabled={loading}
        >
          <TrendingUp className="mr-2 h-4 w-4" />
          Get Suggestions
        </Button>
      </div>

      {loading && (
        <Alert>
          <Brain className="h-4 w-4 animate-pulse" />
          <AlertDescription>
            AI is analyzing your content and generating intelligent recommendations...
          </AlertDescription>
        </Alert>
      )}

      {analysis && (
        <Tabs defaultValue="recommendations" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="recommendations">Recommendations</TabsTrigger>
            <TabsTrigger value="gaps">Content Gaps</TabsTrigger>
            <TabsTrigger value="metrics">Metrics & Insights</TabsTrigger>
          </TabsList>

          <TabsContent value="recommendations" className="space-y-4">
            {(analysis.recommendations || analysis.suggestions)?.map((rec, index) => (
              <Card key={index} className="border-l-4 border-l-primary">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="flex items-center gap-2 text-base">
                      {getTypeIcon(rec.type)}
                      {rec.title}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge variant={getPriorityColor(rec.priority)}>
                        {rec.priority} priority
                      </Badge>
                      {rec.difficulty && (
                        <Badge variant="outline">
                          Level {rec.difficulty}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {rec.description && (
                    <p className="text-sm text-muted-foreground">{rec.description}</p>
                  )}
                  {rec.reason && (
                    <p className="text-sm text-muted-foreground">{rec.reason}</p>
                  )}
                  {rec.estimated_time && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {rec.estimated_time}
                    </div>
                  )}
                  {rec.tags && rec.tags.length > 0 && (
                    <div className="flex gap-1 flex-wrap">
                      {rec.tags.map((tag, tagIndex) => (
                        <Badge key={tagIndex} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="gaps" className="space-y-4">
            {analysis.gaps?.map((gap, index) => (
              <Card key={index}>
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    {getSeverityIcon(gap.severity)}
                    {gap.area}
                    <Badge variant={getPriorityColor(gap.severity)}>
                      {gap.severity} severity
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{gap.description}</p>
                </CardContent>
              </Card>
            ))}
            
            {(!analysis.gaps || analysis.gaps.length === 0) && (
              <Card>
                <CardContent className="flex items-center justify-center py-6">
                  <div className="text-center">
                    <CheckCircle className="h-8 w-8 text-success mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No significant content gaps identified</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="metrics" className="space-y-4">
            {analysis.metrics && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Coverage Score</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{analysis.metrics.coverage_score}%</div>
                    <p className="text-xs text-muted-foreground">Content completeness</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Difficulty Balance</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm font-medium">{analysis.metrics.difficulty_balance}</div>
                    <p className="text-xs text-muted-foreground">Question difficulty distribution</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Content Density</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-sm font-medium">{analysis.metrics.content_density}</div>
                    <p className="text-xs text-muted-foreground">Content per topic ratio</p>
                  </CardContent>
                </Card>
              </div>
            )}

            {analysis.focus_areas && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Focus Areas</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2 flex-wrap">
                    {analysis.focus_areas.map((area, index) => (
                      <Badge key={index} variant="outline">{area}</Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {analysis.learning_objectives && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Learning Objectives</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-1">
                    {analysis.learning_objectives.map((objective, index) => (
                      <li key={index} className="text-sm text-muted-foreground flex items-start gap-2">
                        <Target className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        {objective}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}